import { Injectable, signal } from "@angular/core";
import { InsuranceWritePayload } from "@app/shared/services/enrichment.service";

/** Pending Insurance Master edits owned by a price-edit session. Keeping this
 * outside the tab lets the shared Save bar submit the Master PUT beside the
 * price binding PATCH, and keeps the draft available for Retry. */
@Injectable()
export class InsuranceMasterEditStore {
  private readonly drafts = signal<ReadonlyMap<string, InsuranceMasterDraft>>(
    new Map(),
  );
  private readonly savedLastUpdates = signal<ReadonlyMap<string, string>>(
    new Map(),
  );

  lastUpdate(insuranceId: string, fallback?: string): string | undefined {
    return this.savedLastUpdates().get(insuranceId) ?? fallback;
  }

  recordSaved(insuranceId: string, lastUpdate?: string): void {
    if (!lastUpdate) return;
    const next = new Map(this.savedLastUpdates());
    next.set(insuranceId, lastUpdate);
    this.savedLastUpdates.set(next);
  }

  get(priceId: string): InsuranceMasterDraft | undefined {
    return this.drafts().get(priceId);
  }

  set(priceId: string, draft: InsuranceMasterDraft): void {
    const next = new Map(this.drafts());
    next.set(priceId, draft);
    this.drafts.set(next);
  }

  clear(priceId: string): void {
    if (!this.drafts().has(priceId)) return;
    const next = new Map(this.drafts());
    next.delete(priceId);
    this.drafts.set(next);
  }

  clearAll(): void {
    if (this.drafts().size) this.drafts.set(new Map());
    if (this.savedLastUpdates().size) this.savedLastUpdates.set(new Map());
  }
}

export interface InsuranceMasterDraft {
  id: string;
  payload: InsuranceWritePayload;
  image?: File;
  removeImage?: boolean;
}
